import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';

type Store = { id: string; store_key: string; display_name: string; enabled: boolean; priority: number; status_note: string | null };

export default function AdminStores() {
  const { toast } = useToast();
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('store_settings').select('*').order('priority');
    setStores((data as Store[]) || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const update = (id: string, patch: Partial<Store>) => {
    setStores((prev) => prev.map((s) => s.id === id ? { ...s, ...patch } : s));
  };

  const save = async (s: Store) => {
    setBusy(s.id);
    const { error } = await supabase.from('store_settings').update({
      enabled: s.enabled, priority: s.priority, status_note: s.status_note, display_name: s.display_name,
    }).eq('id', s.id);
    setBusy(null);
    if (error) toast({ title: 'Failed', description: error.message, variant: 'destructive' });
    else toast({ title: 'Saved' });
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading...</div>;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Stores</h2>
      <div className="space-y-3">
        {stores.map((s) => (
          <div key={s.id} className="rounded-lg border bg-card p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-3 sm:w-56">
              <Switch checked={s.enabled} onCheckedChange={(v) => update(s.id, { enabled: v })} />
              <div>
                <div className="font-semibold">{s.display_name}</div>
                <div className="text-xs text-muted-foreground font-mono">{s.store_key}</div>
              </div>
            </div>
            <Input type="number" className="sm:w-24" value={s.priority} onChange={(e) => update(s.id, { priority: Number(e.target.value) })} placeholder="Priority" />
            <Input className="flex-1" value={s.status_note ?? ''} onChange={(e) => update(s.id, { status_note: e.target.value })} placeholder="Status note (e.g., scraper down)" />
            <Button size="sm" disabled={busy === s.id} onClick={() => save(s)}>
              {busy === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Save</>}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}